import { IACListItem } from '@patsnap/synapse_common_interface'
import { useLocale } from '@pharmsnap/shared/src/composition'
import { sharedCtx } from '@pharmsnap/shared/src/context'
import { trackingEvent } from '@pharmsnap/shared/src/features/tracking'
import { createObservable, IHttpWrap } from '@pharmsnap/shared/src/service'
import { IAutoCompleteParams, IAutoCompleteRes, IAutoCompleteType, IListItem, ISearchDataType } from '@pharmsnap/shared/src/types'
import { convertDataStrategyMap } from '@pharmsnap/shared/src/utils'
import { computed, defineComponent, onUnmounted, PropType, Ref, ref, watch } from '@vue/composition-api'
import { Subject, switchMap, tap } from 'rxjs'
import gptLogo from '../../../../assets/svg/gptLogo.svg'
import { BHighlightDom } from '../../BHighlightDom/BHighlightDom'
const getSearchBoostList = async (params: IAutoCompleteParams): Promise<IHttpWrap<IAutoCompleteRes>> => {
  const { key, keywords, limit, data_type, entity_type } = params
  const res = await sharedCtx.service.core?.getSearchBoost({
    key,
    keywords,
    limit,
    cancelToken: params.cancelToken,
    type: params.type,
    data_type,
    entity_type,
  })
  return res
}

export const BSearchBoost = defineComponent({
  name: 'BSearchBoost',
  props: {
    type: {
      type: String as PropType<IAutoCompleteType>,
      default: '',
    },
    dataType: {
      type: String as PropType<ISearchDataType>,
      default: '',
    },
    keywords: {
      type: String,
      default: '',
    },
    entityType: {
      type: Array as PropType<IAutoCompleteParams['entity_type']>,
    },
    emptyTip: {
      type: String,
      default: '',
    },
    // 是否支持智能搜索
    searchBootEmptyTip: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const input$ = new Subject<string>()
    const items = ref([]) as Ref<IACListItem[]>
    const { locale, fieldLang, tsText } = useLocale()
    const showItem = computed(() => {
      const rt = items.value.flatMap((item) => item.data as IListItem[])
      return rt?.[0]
    })
    const loading = ref(true)
    function init() {
      const result$ = input$
        .pipe(
          tap(() => {
            items.value = []
            loading.value = true
          }),
          switchMap((text) => {
            return createObservable<IAutoCompleteParams, IAutoCompleteRes>(getSearchBoostList, {
              keywords: text.trim(),
              key: props.type as IAutoCompleteType,
              data_type: props.dataType,
              entity_type: props.entityType,
            })
          })
        )
        .subscribe((res) => {
          if (res?.success && res.data && props.keywords) {
            const resData = res.data || []
            const func = convertDataStrategyMap.get('Core')

            if (func) {
              const result: IACListItem[] =
                func({
                  data: resData,
                  locale: locale.value,
                  keywords: props.keywords,
                  itemType: props.type,
                }) ?? []
              console.log('result', result)
              items.value = result
            }
          }
          loading.value = false
        })

      onUnmounted(() => {
        result$.unsubscribe()
      })
    }
    function selectItem(item: IListItem) {
      trackingEvent('CLICK_AUTOCOMPLETE_BOOST_RESULT', {
        common1: props.keywords,
        payload: item,
      })
      emit('select', item)
    }
    init()
    watch(
      () => props.keywords,
      (value) => {
        input$.next(value)
      },
      {
        immediate: true,
      }
    )
    return { tsText, showItem, selectItem, locale, fieldLang, loading }
  },
  methods: {
    renderLabel(item: IListItem) {
      const curLangKey: 'display_name_en' | 'display_name_cn' = `display_name_${this.fieldLang}`
      const mainLabel = item[curLangKey] || item.display_name_en || item.display_name_cn
      if (item.suggest && item.suggest !== mainLabel) {
        if (item[curLangKey]) {
          return [item[curLangKey], <BHighlightDom parentheses={true} name={item.suggest} strongArr={item.strongArr}></BHighlightDom>]
        } else {
          return [
            <BHighlightDom name={item.display_name_en || item.display_name_cn}></BHighlightDom>,
            <BHighlightDom parentheses={true} name={item.suggest} strongArr={item.strongArr}></BHighlightDom>,
          ]
        }
      } else {
        return <BHighlightDom name={mainLabel} strongArr={item.strongArr}></BHighlightDom>
      }
    },
  },
  render() {
    if (this.loading) {
      return <div>{this.tsText({ cn: '智能匹配中...', en: 'Intelligent matching in progress...', jp: 'インテリジェントマッチング中...' })}</div>
    }
    if (!this.showItem) {
      return <div>{this.$scopedSlots.searchBootEmpty ? this.$scopedSlots.searchBootEmpty({}) : this.searchBootEmptyTip}</div>
    }
    return (
      <div class="flex items-center gap-2">
        <div class="flex-shrink-0 text-text-t2">{this.emptyTip}</div>
        <img width={18} height={18} src={gptLogo} />
        <span class="flex-shrink-0">{this.tsText({ cn: '您是否想搜', en: 'Do you want to search', jp: 'もしかして' })}</span>
        <span class="truncate text-blue-default cursor-pointer hover:underline" onClick={() => this.selectItem(this.showItem)}>
          {this.renderLabel(this.showItem)}
        </span>
      </div>
    )
  },
})
